import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAISalesDock } from "@/components/AISalesDock";
import {
  GlobeHemisphereEast, SimCard, Users, Printer as Fax, VideoCamera, AddressBook, Receipt,
  IdentificationCard, Wallet, CaretRight, Waveform, ChatCircleText, PhoneCall, FilmSlate, SquaresFour, Gift, Storefront,
  WhatsappLogo, MagnifyingGlass, PaperPlaneTilt, Car, ForkKnife, Truck, ShieldCheck, Briefcase, Buildings, Sparkle, Broadcast, CalendarCheck,
} from "@phosphor-icons/react";

const items = [
  { to: "/app/ai-sales", label: "iYeneh Assistant", icon: Sparkle, sub: "Your 24/7 SI — help, buy & get set up in seconds" },
  { label: "How to use iYeneh", icon: Sparkle, sub: "A quick guided tour — ask and I'll show you now", assistant: true, prompt: "How do I use iYeneh? Show me what I can do and how to get started." },
  { to: "/app/appointments", label: "SI Appointments", icon: CalendarCheck, sub: "SI phone receptionist that books appointments 24/7" },
  { to: "/app/broadcast", label: "Broadcast", icon: Broadcast, sub: "Send voice, SMS & email campaigns" },
  { to: "/app/business", label: "Business Console", icon: Buildings, sub: "Your organization, extensions & SI receptionist" },
  { to: "/app/products", label: "Products & Services", icon: SquaresFour, sub: "Browse everything iYeneh can do" },
  { to: "/app/rides", label: "iYeneh Ride", icon: Car, sub: "Book a ride or drive to earn" },
  { to: "/app/eat", label: "iYeneh Eat", icon: ForkKnife, sub: "Order food, sell or deliver" },
  { to: "/app/fleet", label: "Fleet", icon: Truck, sub: "Track vehicles, fuel & cost per mile" },
  { to: "/app/freelance", label: "Freelance", icon: Briefcase, sub: "Hire talent or sell your skills — wallet escrow" },
  { to: "/app/whatsapp", label: "WhatsApp Business", icon: WhatsappLogo, sub: "Message customers on WhatsApp" },
  { to: "/app/lookup", label: "Number Lookup", icon: MagnifyingGlass, sub: "Carrier, line type & caller ID" },
  { to: "/app/send", label: "Send Money", icon: PaperPlaneTilt, sub: "Send wallet credit to another member" },
  { to: "/app/refer", label: "Refer & Earn", icon: Gift, sub: "Invite friends — you both get credit" },
  { to: "/app/text", label: "Messages", icon: ChatCircleText, sub: "Two-way SMS inbox" },
  { to: "/app/services", label: "Send Top-up", icon: GlobeHemisphereEast, sub: "Mobile, utilities, gift cards, transit & eSIM" },
  { to: "/app/media", label: "Media Studio", icon: FilmSlate, sub: "Audio, video & YouTube for broadcasts" },
  { to: "/app/telephony", label: "Call Settings", icon: PhoneCall, sub: "Forwarding, voicemail & caller ID" },
  { to: "/app/numbers", label: "Buy Number", icon: GlobeHemisphereEast, sub: "Global DID marketplace" },
  { to: "/app/esim", label: "eSIM", icon: SimCard, sub: "Travel data plans" },
  { to: "/app/directory", label: "Company Directory", icon: AddressBook, sub: "Search colleagues & dial extensions" },
  { to: "/app/pbx", label: "Cloud PBX", icon: Users, sub: "Extensions, IVR & SI receptionist" },
  { to: "/app/recordings", label: "Recordings", icon: Waveform, sub: "Vault & SI transcripts" },
  { to: "/app/meet", label: "Meetings", icon: VideoCamera, sub: "Group video & recordings" },
  { to: "/app/sip", label: "SIP Trunking", icon: IdentificationCard, sub: "Channels & routing", soon: true },
  { to: "/app/fax", label: "Cloud Fax", icon: Fax, sub: "Fax PDFs (e.g. to the IRS)" },
  { to: "/app/contacts", label: "Contacts", icon: AddressBook, sub: "Centralised address book", soon: true },
  { to: "/app/billing", label: "Billing", icon: Receipt, sub: "Transactions & invoices" },
  { to: "/app/wallet", label: "Wallet", icon: Wallet, sub: "Balance & ledger" },
];

export default function More() {
  const navigate = useNavigate();
  const { user, isBusiness, isStaff } = useAuth();
  const { open: openAssistant } = useAISalesDock();
  const { data: recs } = useQuery({ queryKey: ["ai-sales-recs"], queryFn: async () => (await api.get("/ai-sales/recommendations")).data, staleTime: 60000 });
  const isReseller = user && ["reseller", "master_distributor", "platform_owner", "admin"].includes(user.role);
  const isInspector = user && ["inspector", "platform_owner", "admin"].includes(user.role);
  let list = items;
  if (isReseller) list = [{ to: "/app/reseller", label: "Reseller Portal", icon: Storefront, sub: "Sign up members & earn commission" }, ...list];
  if (isInspector) list = [{ to: "/app/inspector", label: "Inspector Console", icon: ShieldCheck, sub: "Approve drivers, vehicles & fleets" }, ...list];
  // Paid SI Sales console — only visible to business/enterprise tenants (or staff).
  if (isBusiness || isStaff) list = [{ to: "/app/ai-sales/hunter", label: "SI Sales Team", icon: Storefront, sub: "Find & qualify prospects — the SI sells your catalog 24/7" }, ...list];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">All Services</h1>
        <p className="mt-2 text-muted-foreground">Everything the platform can do, one tap away.</p>
      </div>

      <div className="rounded-3xl border border-primary/30 bg-primary/5 p-5" data-testid="services-recommended">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-primary"><Sparkle size={15} weight="fill" /> Recommended for you</p>
          <button data-testid="services-ask-iyeneh" onClick={() => openAssistant()}
            className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
            <Sparkle size={15} weight="fill" /> Not sure what you need? Ask iYeneh
          </button>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {(recs?.recommended || []).map((r) => (
            <button key={r.key} data-testid="services-rec" onClick={() => openAssistant(r.prompt)}
              className="rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-primary">
              <p className="text-sm font-semibold">{r.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{r.sub}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((it) => (
          <button key={it.to || it.label} data-testid={`more-${it.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => (it.assistant ? openAssistant(it.prompt) : navigate(it.to))}
            className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><it.icon size={22} weight="duotone" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">{it.label}</p>
                {it.soon && <span className="rounded-full bg-secondary px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Soon</span>}
              </div>
              <p className="truncate text-xs text-muted-foreground">{it.sub}</p>
            </div>
            <CaretRight size={16} className="text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
